import { useState, useEffect } from 'react';
import { VStack, Text, Heading, HStack, ScrollView } from 'native-base';

// Assets
import { theme } from '@breakroom-nx/assets';

// Redux
import {
  useAppDispatch,
  closeModal,
  openModal,
  useUploadUserPhotoMutation,
  useUpdateUserDataMutation,
  useGetCurrentUserQuery,
  useUpdateUserAccountStatesMutation,
} from '@breakroom-nx/redux';

// Components
import {
  ModalWrapper,
  PrimaryButton,
  TertiaryButton,
  TextView,
  UserAvatar,
  PointsTag,
  ModifyProfilePhoto,
} from '../../';

// Types
import { Shuffler, shuffle } from '@breakroom-nx/classes';
import { InputType, Status, PermissionStatus } from '@breakroom-nx/helpers';

// Firebase
import { getAuth } from 'firebase/auth';

// Move to Database Later
const anonymousFeatures = [
  'You cannot add friends or send private messages.',
  'If you clear your browser history or cookies, you will lose your account and all the information associated with it.',
  'You cannot gain points for sending messages or contributing to the community.',
  'You are fully anonymous, this is a burner account.',
  'You can convert to a full account at any time.',
  'You can still activate anonymous mode with a full account.',
];

const platformRules = [
  'Promoting the harm of other users is not allowed.',
  'Condoning violence and attacks of any magnitude are not allowed.',
  'Promoting the request or exchange of illicit services, substances or sexual favors is not allowed.',
  'Promoting the disclosure or exchange of confidential information (e.g. addresses of users, their names and locations) without their consent is not allowed.',
  'Promoting personal services, including the sharing of links to your other personals is frowned upon. Please be respectful and do not spam users with your services if you were not asked to disclose them.',
  'Even in anonymous mode, you are expected to adhere to these rules. You can still be reported and depending on the severity of your violation, could result in your removal from the platform.',
  'The rules are not limited to what is written here, we are here to build a thriving community. Be mindful of others; please use your common sense, respect your fellow human beings and have fun. ',
];

// props: any
export const ProfileSetup = () => {
  const appDispatch = useAppDispatch();

  const { data: currentUser, isLoading: isLoadingUser } =
    useGetCurrentUserQuery();

  const [bioText, setBioText] = useState<string | undefined>(undefined);
  const auth = getAuth();
  const [shuffler, setShuffler] = useState<Shuffler>();
  const [showAnonymousAvatar, setShowAnonymousAvatar] = useState(false);
  const [image, setImage] = useState<string | undefined>(currentUser?.avatar);
  const [uploading, setUploading] = useState(false);

  const [updateUserData] = useUpdateUserDataMutation();
  const [updateAccountStates] = useUpdateUserAccountStatesMutation();
  const [uploadUserPhoto, uploadedPhotoState] = useUploadUserPhotoMutation();

  useEffect(() => {
    if (!showAnonymousAvatar) {
      const timeout = setTimeout(() => {
        setShuffler(shuffle());
      }, 500);
      return () => clearTimeout(timeout);
    } else {
      return;
    }
  }, [showAnonymousAvatar]);

  useEffect(() => {
    if (shuffler) {
      setShowAnonymousAvatar(true);
    }
  }, [shuffler]);

  const textUpdated = (text: string) => {
    setBioText(text);
  };

  const imageUpdated = (image: string) => {
    setImage(image);
  };

  const finalizeOnboarding = async () => {
    setUploading(true);

    if (currentUser?.uid) {
      if (image) {
        await uploadUserPhoto(image).catch((err) => {
          appDispatch(
            openModal({
              modalType: 'Alert',
              modalProps: {
                status: Status.error,
                title: 'Error uploading your photo',
                description: err,
              },
            })
          );
        });
      }

      await updateUserData({
        uid: currentUser.uid,
        bio: bioText,
      });

      await updateAccountStates({
        completedOnboarding: true,
      });
    }

    setUploading(false);
    appDispatch(closeModal());

    if (
      !currentUser?.cookiePermissions ||
      currentUser.cookiePermissions === PermissionStatus.NotSet ||
      !currentUser?.notificationPermissions ||
      currentUser.notificationPermissions === PermissionStatus.NotSet ||
      !currentUser?.locationPermissions ||
      currentUser.locationPermissions === PermissionStatus.NotSet
    ) {
      appDispatch(openModal({ modalType: 'PermissionsContainer' }));
    }
  };

  useEffect(() => {}, [image]);

  return (
    <ModalWrapper disabled>
      <VStack space={5}>
        <VStack
          bg={'surface.100'}
          space={3}
          textAlign={'center'}
          style={{
            borderColor: theme.colors.surface[200],
            borderWidth: 1,
            borderRadius: 25,
            shadowColor: theme.colors.surface[100],
            shadowOpacity: 0.25,
            shadowRadius: 20,
            shadowOffset: {
              width: 10,
              height: 20,
            },
            padding: 30,
          }}
        >
          {auth.currentUser?.isAnonymous ? (
            <UserAvatar
              disabled
              noBorder
              noMargin
              uid={currentUser?.uid ?? ''}
              size={100}
            />
          ) : null}

          <Heading fontSize={30} lineHeight={34}>
            {`Welcome, `}
            <Text color={currentUser?.theme ?? shuffler?.color}>
              {currentUser?.username}
            </Text>
            !
          </Heading>
          <Text color={'surface.600'} fontSize={16}>
            {auth.currentUser?.isAnonymous
              ? 'Take a moment to familiarize yourself with the features of an anonymous account and the rules of the platform.'
              : 'Take a moment to set up your profile! Add a picture, a little about yourself and familiarize yourself with the rules of the platform.'}
          </Text>
        </VStack>

        {auth.currentUser?.isAnonymous ? (
          <VStack
            textAlign={'center'}
            space={3}
            bg={'surface.100'}
            style={{
              borderRadius: 25,
              borderWidth: 1,
              borderColor: theme.colors.surface[200],
              shadowColor: theme.colors.surface[100],
              shadowOpacity: 0.25,
              shadowRadius: 20,
              shadowOffset: {
                width: 10,
                height: 20,
              },
              padding: 30,
            }}
          >
            <VStack space={'5'}>
              <VStack alignItems={'flex-start'} space={5}>
                <Text fontWeight={'bold'}>Anonymous Account Features</Text>
                <VStack space={2} ml={3} alignItems={'flex-start'}>
                  {anonymousFeatures.map((feature, index) => {
                    return (
                      <Text
                        color={'surface.600'}
                        textAlign={'left'}
                        key={index}
                      >{`• ${feature}`}</Text>
                    );
                  })}
                </VStack>
              </VStack>
              <VStack alignItems={'flex-start'} space={5}>
                <Text fontWeight={'bold'}>Platform Rules</Text>
                <VStack space={2} ml={3}>
                  {platformRules.map((rule, index) => {
                    return (
                      <Text
                        color={'surface.600'}
                        textAlign={'left'}
                        key={index}
                      >{`• ${rule}`}</Text>
                    );
                  })}
                </VStack>
              </VStack>
              <VStack space={3}>
                <PrimaryButton
                  isLoading={uploading}
                  disabled={uploading}
                  text={'I Understand'}
                  onPress={() => {
                    finalizeOnboarding();
                  }}
                />
              </VStack>
            </VStack>
          </VStack>
        ) : (
          <VStack
            bg={'surface.100'}
            space={3}
            textAlign={'center'}
            style={{
              borderColor: theme.colors.surface[200],
              borderWidth: 1,
              shadowColor: theme.colors.surface[100],
              shadowOpacity: 0.25,
              shadowRadius: 20,
              shadowOffset: {
                width: 10,
                height: 20,
              },
              padding: 30,
            }}
          >
            <VStack space={5}>
              <ModifyProfilePhoto
                showPoints={true}
                onPhotoSelected={imageUpdated}
              />
              <VStack textAlign={'left'} space={10}>
                <VStack>
                  <HStack justifyContent={'space-between'}>
                    <Text
                      fontFamily={'heading'}
                      fontSize={23}
                      color={'white'}
                      fontWeight={600}
                      mb={'3px'}
                    >
                      About Me
                    </Text>
                    <PointsTag amount={10} payment />
                  </HStack>
                  <TextView
                    height={'100px'}
                    type={InputType.Text}
                    placeholder={'Write a bio'}
                    isRequired={false}
                    onChangeText={textUpdated}
                  />
                </VStack>
              </VStack>

              <VStack alignItems={'flex-start'} space={5}>
                <Text fontWeight={'bold'}>Platform Rules</Text>
                <VStack space={2} ml={3}>
                  {platformRules.map((rule, index) => {
                    return (
                      <Text
                        color={'surface.600'}
                        textAlign={'left'}
                        key={index}
                      >{`• ${rule}`}</Text>
                    );
                  })}
                </VStack>
              </VStack>

              <VStack space={3}>
                <PrimaryButton
                  disabled={
                    (image === undefined &&
                      (bioText === '' || bioText === undefined)) ||
                    uploading
                  }
                  isLoading={uploading}
                  text={'Finalize Account'}
                  onPress={() => {
                    finalizeOnboarding();
                  }}
                />

                <TertiaryButton
                  disabled={uploading}
                  color={theme.colors.surface[400]}
                  text={'Maybe later'}
                  onPress={() => {
                    appDispatch(closeModal());
                  }}
                />
              </VStack>
            </VStack>
          </VStack>
        )}
      </VStack>
    </ModalWrapper>
  );
};
